import sleep from "../helpers/sleep";
import { logger } from "./loggingService";
import { CalculationType } from "../@types/enums";

export const FACE_IDEAL_API: CalculationType   = CalculationType.IDEAL_MULTI;
export const MODULE_GRAPH:    CalculationType   = CalculationType.MODULE_GRAPH;
export const SHADE_CALCULATIONS: CalculationType = CalculationType.SHADE;
export const ACCESS_MULTIPLIERS: CalculationType = CalculationType.ACCESS_MULTI;

class SiteCalculationState {
    calculations: {
        [value in CalculationType]?: {
            calculate: () => void,
            needsUpdate: any[],
            calculatingPromise: any,
            eventTarget: EventTarget,
        } 
    };

    constructor() {
        this.calculations = {};
    }

    async registerCalculation(name: CalculationType, func: any, options: { needsUpdate: (number | boolean)[], calculateNow?: boolean }) {
        logger.debug({name}, 'registered calculation');

        this.calculations[name] = {
            calculate: async () => {
                const calculationObj = this.calculations[name];
                if (!calculationObj) return;
                if (calculationObj.calculatingPromise) {
                    // calculation already running
                    return await calculationObj.calculatingPromise;
                }

                try {
                    while (calculationObj.needsUpdate.length) {
                        // copy queue
                        const updates = [...calculationObj.needsUpdate];
                        calculationObj.needsUpdate = [];

                        // Set calculatingPromise BEFORE invoking func so that
                        // any synchronous re-entry (e.g. Zustand setState inside
                        // func triggering a React re-render that calls calculate
                        // again) hits the guard above instead of looping.
                        let resolveGuard: () => void;
                        calculationObj.calculatingPromise = new Promise<void>(r => { resolveGuard = r; });

                        try {
                            await Promise.resolve(func({ needsUpdate: updates }));
                        } finally {
                            resolveGuard!();
                        }
                    }

                    this.setUpdated(name);
                } finally {
                    calculationObj.calculatingPromise = null;
                }
            },
            needsUpdate: [],
            calculatingPromise: null,
            eventTarget: new EventTarget(),
        };

        if(!this.calculations[name]) {
            logger.debug({}, 'this.calculations[name] is undefined')
            return;
        }

        if (options?.needsUpdate !== undefined) {
            if (Array.isArray(options.needsUpdate)) {
                this.calculations[name]!.needsUpdate = [...options.needsUpdate];
            } else {
                this.calculations[name]!.needsUpdate = [options.needsUpdate];
            }
        }

        if (options?.calculateNow) {
            await this.calculations[name]!.calculate();
        }
    }

    clearNeedsUpdate(name: CalculationType) {
        const calculation = this.calculations[name];
        if (!calculation) return;
        calculation.needsUpdate = [];
    }

    needsUpdate(name: CalculationType, data?: number) {
        if(!this.calculations[name]) {
            logger.debug({}, 'this.calculations[name] is undefined')
            return;
        }
        if (!this.isRegistered(name)) {
            logger.debug({name, data}, 'needs update calculation not registered');
            return;
        }

        logger.debug({name, data}, 'needs update');

        this.calculations[name]!.needsUpdate.push(data);
        this.calculations[name]!.eventTarget.dispatchEvent(new CustomEvent('needsUpdate', {
            detail: {
                name: name,
                data: data,
            },
        }));
    }

    doesNeedUpdateOrUpdating(name: CalculationType) {
        if (!this.calculations[name]) {
            return false;
        }
        return this.isRegistered(name)
            && (this.calculations[name]!.needsUpdate.length > 0 || 
                this.calculations[name]!.calculatingPromise !== null);
    }

    setUpdated(name: CalculationType) {
        if(!this.calculations[name]) {
            logger.debug({}, 'this.calculations[name] is undefined')
            return;
        }
        if (!this.isRegistered(name)) {
            logger.debug({name}, 'updated calculation not registered');
            return;
        }

        logger.debug({name}, 'updated');

        this.calculations[name]!.needsUpdate = [];
        this.calculations[name]!.eventTarget.dispatchEvent(new CustomEvent('updated', {
            detail: {
                name: name,
            },
        }));
    }

    async useCalculations(...names: CalculationType[]) {
        const promises = [];
        for (const name of names) {
            const calculation = this.calculations[name];
            if (calculation) {
                if (this.doesNeedUpdateOrUpdating(name)) {
                    promises.push(calculation.calculate());
                }
            } else {
                logger.debug({name}, 'useCalculations not registered');

                promises.push((async () => {
                    while (!this.calculations[name]) {
                        await sleep(100);
                    }

                    const calculation = this.calculations[name];
                    if (calculation?.needsUpdate.length) {
                        await calculation.calculate();
                    }
                })());
            }
        }

        await Promise.all(promises);
    }

    isRegistered(name: CalculationType) {
        return this.calculations[name] !== undefined;
    }

    addUpdatedListener(name: CalculationType, func: (e: any) => void) {
        this.calculations[name]?.eventTarget?.addEventListener('updated', func);
    }

    removeUpdatedListener(name: CalculationType, func: (e: any) => void) {
        this.calculations[name]?.eventTarget?.removeEventListener('updated', func);
    }

    addNeedsUpdateListener(name: CalculationType, func: (e: any) => void) {
        this.calculations[name]?.eventTarget?.addEventListener('needsUpdate', func);
    }

    removeNeedsUpdateListener(name: CalculationType, func: (e: any) => void) {
        this.calculations[name]?.eventTarget?.removeEventListener('needsUpdate', func);
    }

    reset() {
        this.calculations = {};
    }
}

export const siteCalculationState = new SiteCalculationState();

/** Calculation types that must finish before production values (PV Watts) are shown. */
export const PRODUCTION_CALCULATION_TYPES: CalculationType[] = [
    CalculationType.ACCESS_MULTI,
    CalculationType.IDEAL_MULTI,
    CalculationType.MODULE_GRAPH,
];

/** True when no production-related calculation is pending/in-flight (same gate as Production panel). */
export function isProductionCalculationReady(): boolean {
    for (const name of PRODUCTION_CALCULATION_TYPES) {
        if (siteCalculationState.doesNeedUpdateOrUpdating(name)) return false;
    }
    return true;
}
